function findEqualPanels(width: number, sub: number, numberOfPanels: number) {
  return (width - sub) / numberOfPanels;
}

type Args = {
  width: string;
  widthFraction: string;
  direction: string;
  swingType: string;
  sideLite: string;
  pWidth: string;
  pWidthFraction: string;
  equalPanels: boolean;
  material: string;
  isPivot: boolean;
  screenType: string;
  thickAlum?: boolean;
  isPico: boolean;
  exactConfig: string;
};

function calcSwingWidths(swingArgs: Args) {
  const {
    width,
    widthFraction,
    direction,
    swingType,
    sideLite,
    pWidth,
    pWidthFraction,
    equalPanels,
    material,
    isPivot,
    screenType,
    isPico,
    exactConfig,
  } = swingArgs;

  let panelW = 0;
  let slw = 0;
  let equalized = 0;

  let fraction =
    widthFraction === "0" || !widthFraction ? "" : widthFraction.split("/");
  let overall =
    fraction !== ""
      ? parseInt(width) + parseInt(fraction[0]) / parseInt(fraction[1])
      : parseInt(width);

  let pfraction =
    pWidthFraction === "0" || !pWidthFraction ? "" : pWidthFraction.split("/");
  let pOverall =
    pfraction !== ""
      ? parseInt(pWidth) + parseInt(pfraction[0]) / parseInt(pfraction[1])
      : parseInt(pWidth);

  function materialAdder(adderObj: {
    wood: number;
    clad: number;
    alum: number;
  }) {
    if (material === "All Wood") {
      return adderObj.wood;
    }

    if (material.includes("Clad")) {
      return adderObj.clad;
    }

    if (material === "All Aluminum") {
      return adderObj.alum;
    }

    return 0;
  }

  if (isPivot) {
    switch (sideLite) {
      case "Add Single Sidelite":
        // let materialSub1 = 4; changed on 04/18/2024 from Brian
        let materialSub1 = 4.0625;
        !equalPanels
          ? (slw = overall - pOverall - materialSub1)
          : (equalized = findEqualPanels(overall, materialSub1, 2));
        break;
      case "Add Double Sidelite":
        // let materialSub2 = 5.875; changed on 04/18/2024 from Brian
        let materialSub2 = 5.9375;
        !equalPanels
          ? (slw = (overall - pOverall - materialSub2) / 2)
          : (equalized = findEqualPanels(overall, materialSub2, 3));
        break;
      default:
        // let materialSub3 = 2.125; changed on 04/18/2024 from Brian
        let materialSub3 = 2.1875;
        !equalPanels
          ? (panelW = overall - materialSub3)
          : (equalized = findEqualPanels(overall, materialSub3, 1));
    }
  } else if (direction === "Outswing") {
    const jambLeg = screenType?.includes("Centor") ? 6.25 : 0;

    if (swingType === "Single Swing") {
      switch (sideLite) {
        case "Add Single Sidelite":
          if (!isPico) {
            const oneSl = materialAdder({
              wood: 2.875,
              clad: 2.9374,
              alum: swingArgs.thickAlum ? 3.84375 : 3.09375,
            });
            !equalPanels
              ? (slw = overall - jambLeg - pOverall - oneSl)
              : (equalized = findEqualPanels(overall - jambLeg, oneSl, 2));
          } else {
            //Primary Hinge Side Jamb: 1.125 (1 1/8)
            //Primary Hinge Side Gap: 0.1875 (3/16)
            //Lock Side Jamb: 0.875 (7/8)
            //Lock Side Gap: 0.1875 (3/16)
            //Sidelite non Hinge Side Jamb: 0.875 (7/8))
            //Total: 3.25
            !equalPanels
              ? (slw = overall - pOverall - 3.25)
              : (equalized = findEqualPanels(overall, 3.25, 2));
          }
          break;
        case "Add Double Sidelite":
          if (!isPico) {
            const twoSl = materialAdder({
              wood: 3.75,
              clad: 3.8124,
              alum: swingArgs.thickAlum ? 5.375 : 4.625,
            });
            !equalPanels
              ? (slw = (overall - jambLeg - pOverall - twoSl) / 2)
              : (equalized = findEqualPanels(overall - jambLeg, twoSl, 3));
          } else {
            //Primary Hinge Side Jamb: 1.125 (1 1/8)
            //Primary Hinge Side Gap: 0.1875 (3/16)
            //Lock Side Jamb: 0.875 (7/8)
            //Lock Side Gap: 0.1875 (3/16)
            //Sidelite non Hinge Side Jamb: 0.875 (7/8))
            //Sidelite Outer Side Jamb (Other Side): 0.875 (7/8))
            //Total:4.125
            !equalPanels
              ? (slw = (overall - pOverall - 4.125) / 2)
              : (equalized = findEqualPanels(overall, 4.125, 3));
          }
          break;
        default:
          if (!isPico) {
            const noSl = materialAdder({
              wood: 2,
              clad: 2.0624,
              alum: swingArgs.thickAlum ? 2.3125 : 1.5625,
            });
            !equalPanels
              ? (panelW = overall - jambLeg - noSl)
              : (equalized = findEqualPanels(overall, noSl, 1));
          } else {
            //Primary Hinge Side Jamb: 1.125 (1 1/8)
            //Primary Hinge Side Gap: 0.1875 (3/16)
            //Lock Side Jamb: 0.875 (7/8)
            //Lock Side Gap: 0.1875 (3/16)
            //Total: 2.375
            !equalPanels
              ? (panelW = overall - 2.375)
              : (equalized = findEqualPanels(overall, 2.375, 1));
          }
      }
    } else if (swingType === "Pair Swing") {
      switch (sideLite) {
        case "Add Single Sidelite": //only for aluminum so far
          const oneSl = materialAdder({
            wood: 0,
            clad: 0,
            alum: swingArgs.thickAlum ? 4.09375 : 3.34375,
          });
          !equalPanels
            ? (slw = overall - jambLeg - 2 * pOverall - oneSl)
            : (equalized = findEqualPanels(overall - jambLeg, oneSl, 4));
          break;
        case "Add Double Sidelite":
          if (!isPico) {
            const twoSl = materialAdder({
              wood: 4.375,
              clad: 4.4374,
              alum: swingArgs.thickAlum ? 5.625 : 4.875,
            });
            !equalPanels
              ? (slw = (overall - jambLeg - 2 * pOverall - twoSl) / 2)
              : (equalized = findEqualPanels(overall - jambLeg, twoSl, 4));
          } else {
            //Primary Hinge Side Jamb: 1.125 (1 1/8)
            //Primary Hinge Side Gap: 0.1875 (3/16)
            //Middle Gap: 0.125 (1/8)
            //Secondary Hinge Side Jamb: 1.125 (1 1/8)
            //Secondary Hinge Side Gap: 0.1875 (3/16)
            //Sidelite Outer Side Jamb: 0.875 (7/8))
            //Sidelite Outer Side Jamb (Other Side): 0.875 (7/8))
            //Total: 4.5
            !equalPanels
              ? (slw = (overall - 2 * pOverall - 4.5) / 2)
              : (equalized = findEqualPanels(overall, 4.5, 4));
          }
          break;
        default:
          if (!isPico) {
            const noSl = materialAdder({
              wood: 2.625,
              clad: 2.6874,
              alum: swingArgs.thickAlum ? 2.5625 : 1.8125,
            });
            !equalPanels
              ? (panelW = (overall - jambLeg - noSl) / 2)
              : (equalized = findEqualPanels(overall, noSl, 2));
          }else{
            //Primary Hinge Side Jamb: 1.125 (1 1/8)
            //Primary Hinge Side Gap: 0.1875 (3/16)
            //Middle Gap: 0.125 (1/8)
            //Secondary Hinge Side Jamb: 1.125 (1 1/8)
            //Secondary Hinge Side Gap: 0.1875 (3/16)
            //Total: 2.75
            !equalPanels
              ? (panelW = (overall - 2.75) / 2)
              : (equalized = findEqualPanels(overall, 2.75, 2));
          }
      }
    }
  } else if (direction === "Inswing") {
    if (swingType === "Single Swing") {
      switch (sideLite) {
        case "Add Single Sidelite":
          if (!isPico) {
            const oneSl = materialAdder({
              wood: 3.5625,
              clad: 3.9374,
              alum: swingArgs.thickAlum ? 3.84375 : 3.09375,
            });
            !equalPanels
              ? (slw = overall - pOverall - oneSl)
              : (equalized = findEqualPanels(overall, oneSl, 2));
          } else {
            //Primary Hinge Side Jamb: 0.9375 (15/16)
            //Primary Hinge Side Gap: 0.0937 (3/32)
            //Lock Side Gap: 0.0937 (3/32)
            //Lock Side Jamb: 0.9375 (15/16)
            //Sidelite Jamb: 0.9375 (15/16)
            //Sidelite Jamb (Other Side):  0.9375 (15/16)
            //Total: 3.9374
            !equalPanels
              ? (slw = overall - pOverall - 3.9374)
              : (equalized = findEqualPanels(overall, 3.9374, 2));
          }
          break;
        case "Add Double Sidelite":
          if (!isPico) {
            const twoSl = materialAdder({
              wood: 5.43755,
              clad: 5.8124,
              alum: swingArgs.thickAlum ? 5.375 : 4.625,
            });
            !equalPanels
              ? (slw = (overall - pOverall - twoSl) / 2)
              : (equalized = findEqualPanels(overall, twoSl, 3));
          } else {
            //Primary Hinge Side Jamb: 0.9375 (15/16)
             //Primary Hinge Side Gap: 0.0937 (3/32)
            //Lock Side Gap: 0.0937 (3/32)
            //Lock Side Jamb: 0.9375 (15/16)
            //Sidelite #1 Jamb: 0.9375 (15/16)
            //Sidelite #1 Jamb (Other Side):  0.9375 (15/16)
            //Sidelite #2 Jamb: 0.9375 (15/16)
            //Sidelite #2 Jamb (Other Side):  0.9375 (15/16)
            //Total: 5.8124
            !equalPanels
              ? (slw = (overall - pOverall - 5.8124) / 2)
              : (equalized = findEqualPanels(overall, 5.8124, 3));
          }
          break;
        default:
          if (!isPico) {
            const noSl = materialAdder({
              wood: 1.6875,
              clad: 2.0624,
              alum: swingArgs.thickAlum ? 2.3125 : 1.5625,
            });
            !equalPanels
              ? (panelW = overall - noSl)
              : (equalized = findEqualPanels(overall, noSl, 1));
          } else {
            //Primary Hinge Side Jamb: 0.9375 (15/16)
            //Primary Hinge Side Gap: 0.0937 (3/32)
            //Lock Side Jamb: 0.0937 (3/32)
            //Lock Side Gap: 0.9375 (15/16)
            //Total: 2.0614
            !equalPanels
              ? (panelW = overall - 2.0614)
              : (equalized = findEqualPanels(overall, 2.0614, 1));
          }
      }
    } else if (swingType === "Pair Swing") {
      switch (sideLite) {
        case "Add Single Sidelite": //only for aluminum so far
          const oneSl = materialAdder({
            wood: 0,
            clad: 0,
            alum: swingArgs.thickAlum ? 4.09375 : 3.34375,
          });
          !equalPanels
            ? (slw = overall - 2 * pOverall - oneSl)
            : (equalized = findEqualPanels(overall, oneSl, 4));
          break;
        case "Add Double Sidelite":
          if (!isPico) {
            const twoSl = materialAdder({
              wood: 6.0625,
              clad: 6.4374,
              alum: swingArgs.thickAlum ? 5.625 : 4.875,
            });
            !equalPanels
              ? (slw = (overall - 2 * pOverall - twoSl) / 2)
              : (equalized = findEqualPanels(overall, twoSl, 4));
          } else {
            //Primary Hinge Side Jamb: 0.9375 (15/16)
            //Primary Hinge Side Gap: 0.0937 (3/32)
            //Secondary Hinge Side Jamb: 0.9375 (15/16)
            //Secondary Hinge Side Gap: 0.0937 (3/32)
            //Middle Gap: 0.0937 (3/32)
            //Sidelite #1 Jamb: 0.9375 (15/16)
            //Sidelite #1 Jamb (Other Side):  0.9375 (15/16)
            //Sidelite #2 Jamb: 0.9375 (15/16)
            //Sidelite #2 Jamb (Other Side):  0.9375 (15/16)
            //Total: 5.9061
            !equalPanels
              ? (slw = (overall - 2 * pOverall - 5.9061) / 2)
              : (equalized = findEqualPanels(overall, 5.9061, 4));
          }
          break;
        default:
          if (!isPico) {
            const noSl = materialAdder({
              wood: 2.3125,
              clad: 2.6875,
              alum: swingArgs.thickAlum ? 2.5625 : 1.8125,
            });
            !equalPanels
              ? (panelW = (overall - noSl) / 2)
              : (equalized = findEqualPanels(overall, noSl, 2));
          } else {
            //Hinge Side Jamb #1: 0.9375 (15/16)
            //Primary Hinge Side Gap #1: 0.0937 (3/32)
            //Middle Gap: 0.0937 (3/32)
            //Hinge Side Jamb #2: 0.9375 (15/16)
            //Primary Hinge Side Gap #2: 0.0937 (3/32)
            //Total: 2.1561
            !equalPanels
              ? (panelW = (overall - 2.1561) / 2)
              : (equalized = findEqualPanels(overall, 2.1561, 2));
          }
      }
    }
  }

  return {
    panelW,
    slw,
    equalized,
  };
}

export default calcSwingWidths;
